import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, ElementRef, ViewChild, AfterViewInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ApiService } from '@app/shared/services/api.service';
import { CommentService } from '@app/operation/comments-modal/comment.service';
import { OperationService } from '@app/operation/operationanalysis/operationanalysis.service';
import { UtilService } from '@app/shared/services/util.service';
import { ToastrService } from 'ngx-toastr';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '@env/environment';
import Quill from 'quill';
import * as mammoth from 'mammoth';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-ticket-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, MatIconModule, MatTooltipModule],
  templateUrl: './ticket-detail.component.html',
  styleUrls: ['./ticket-detail.component.css']
})
export class TicketDetailComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() ticket: any;
  @Input() isSuperAdmin: boolean = true;
  @Output() backToList = new EventEmitter<void>();
  @Output() ticketUpdated = new EventEmitter<any>();
  @Output() editRequested = new EventEmitter<any>();

  @ViewChild('ticketAttachmentInput') ticketAttachmentInput!: ElementRef<HTMLInputElement>;
  @ViewChild('threadAttachmentInput') threadAttachmentInput!: ElementRef<HTMLInputElement>;

  private quill: any = null;
  private mainEditorHost: HTMLElement | null = null;
  private threadQuill: any = null;
  private threadEditorHost: HTMLElement | null = null;
  private destroy$ = new Subject<void>();
  private readonly COMMENT_ATTACHMENT_FOLDER = 'user_attachments';

  @ViewChild('ticketEditorRef')
  set ticketEditorRefSetter(el: ElementRef | undefined) {
    const host = el?.nativeElement ?? null;
    if (!host) {
      this.mainEditorHost = null;
      this.quill = null;
      return;
    }
    if (this.quill && this.mainEditorHost === host) {
      return;
    }
    host.innerHTML = '';
    this.mainEditorHost = host;
    this.quill = new Quill(host, {
      theme: 'snow',
      placeholder: 'Send a message... (Enter to send, Shift+Enter for newline)',
      modules: { toolbar: this.QUILL_TOOLBAR }
    });

    this.bindMentionSupport(this.quill, 'main');

    this.quill.root.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.sendTicketReply();
      }
    });
  }

  focusMainEditor(): void {
    if (this.quill) {
      this.quill.focus();
    }
  }

  private readonly QUILL_TOOLBAR = [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ];

  ticketConversations: any[] = [];
  activeThread: any = null;

  selectedAttachments: File[] = [];
  threadAttachments: File[] = [];

  attachmentPreviewOpen: boolean = false;
  previewLoading: boolean = false;
  previewFileName: string = '';
  previewFileType: string = '';
  previewMode: string = 'unsupported';
  previewUrl: string = '';
  previewResourceUrl: SafeResourceUrl | null = null;
  previewText: string = '';
  previewCsvHeaders: string[] = [];
  previewCsvRows: string[][] = [];
  previewJsonText: string = '';
  previewDocxText: string = '';
  previewExcelHeaders: string[] = [];
  previewExcelRows: string[][] = [];
  previewMessage: string = '';
  previewAttachment: any = null;

  mentionUsers: any[] = [];
  mentionMenuOpen: boolean = false;
  mentionMenuScope: 'main' | 'thread' = 'main';
  mentionMenuQuery: string = '';
  mentionRange: any = null;
  mentionInsertMode: 'insert' | 'replace' = 'replace';
  mentionUserIds: Array<string | number> = [];
  threadMentionUserIds: Array<string | number> = [];
  private isInteractingWithMentionMenu = false;

  isSendingReply: boolean = false;
  isSendingThreadReply: boolean = false;
  isUpdatingProperties: boolean = false;

  selectedTicketStatus: number = 2;
  selectedTicketPriority: number = 1;
  selectedTicketType: string = '--';
  assignedToEmail: string = '';

  readonly ALLOWED_ATTACHMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv,.rtf,.odt,image/png,image/jpeg,image/gif,application/pdf';

  readonly EMOJI_GROUPS = [
    {
      label: 'Reactions',
      emojis: ['👍', '✅', '❌', '❤️', '🔥', '👏', '😂', '😮', '😢', '🎉'],
    },
  ];

  @ViewChild('threadEditorRef')
  set threadEditorRefSetter(el: ElementRef | undefined) {
    const host = el?.nativeElement ?? null;
    if (!host) {
      this.threadEditorHost = null;
      this.threadQuill = null;
      return;
    }
    if (this.threadQuill && this.threadEditorHost === host) {
      return;
    }
    host.innerHTML = '';
    this.threadEditorHost = host;
    this.threadQuill = new Quill(host, {
      theme: 'snow',
      placeholder: 'Reply in thread...',
      modules: { toolbar: this.QUILL_TOOLBAR },
    });
    this.bindMentionSupport(this.threadQuill, 'thread');
    this.threadQuill.root.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.submitThreadReply();
      }
    });
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.viewMenuOpen = false;
    this.ticketConversations.forEach(c => {
      c.showReactions = false;
      c.hovered = false;
      if (c.replies) {
        c.replies.forEach((r: any) => {
          r.showReactions = false;
          r.hovered = false;
        });
      }
    });
    if (this.activeThread) {
      this.activeThread.showReactions = false;
      this.activeThread.hovered = false;
      if (this.activeThread.replies) {
        this.activeThread.replies.forEach((r: any) => {
          r.showReactions = false;
          r.hovered = false;
        });
      }
    }
  }

  constructor(
    private http: HttpClient,
    private api: ApiService,
    private commentService: CommentService,
    private operationService: OperationService,
    private util: UtilService,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  private getFreshdeskHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': 'Basic ' + btoa(environment.FRESHDESK_API_KEY + ':X'),
      'Content-Type': 'application/json'
    });
  }

  ngOnInit(): void {
    this.syncTicketState();
    this.startPolling();
    this.loadStakeholders();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ticket'] && this.ticket) {
      this.activeThread = null;
      this.syncTicketState();
      this.loadStakeholders();
    }
  }

  activeTab: 'comments' | 'activities' = 'comments';
  viewMenuOpen: boolean = false;

  toggleViewMenu(event: Event): void {
    event.stopPropagation();
    this.viewMenuOpen = !this.viewMenuOpen;
  }

  switchViewTab(tab: 'comments' | 'activities'): void {
    this.activeTab = tab;
    this.viewMenuOpen = false;
  }

  showActivities: boolean = true;

  toggleActivities(): void {
    this.showActivities = !this.showActivities;
  }

  formatTimeAgo(dateStr: string | Date): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.max(0, Math.floor(diffMs / 1000));
    const diffMin = Math.floor(diffSec / 60);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffMonths = Math.floor(diffDays / 30);

    let agoStr = '';
    if (diffMonths >= 1) agoStr = `${diffMonths} ${diffMonths === 1 ? 'month' : 'months'} ago`;
    else if (diffDays >= 1) agoStr = `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    else if (diffHours >= 1) agoStr = `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    else if (diffMin >= 1) agoStr = `${diffMin} ${diffMin === 1 ? 'min' : 'mins'} ago`;
    else agoStr = 'Just now';

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayName = days[date.getDay()];
    const monthName = months[date.getMonth()];
    const dateNum = date.getDate();
    const year = date.getFullYear();
    const hoursRaw = date.getHours();
    const mins = String(date.getMinutes()).padStart(2, '0');
    const ampm = hoursRaw >= 12 ? 'PM' : 'AM';
    const hours = hoursRaw % 12 || 12;

    const formattedDate = `${dayName}, ${dateNum} ${monthName} ${year} at ${hours}:${mins} ${ampm}`;
    return `${agoStr} (${formattedDate})`;
  }

  getEnvironmentName(): string {
    const host = window.location.hostname;
    if (host.includes('qa')) return 'qa';
    if (host.includes('uat')) return 'uat';
    if (host.includes('dev') || host.includes('localhost')) return 'dev';
    return 'prod';
  }

  getTicketActivities(): any[] {
    if (!this.ticket) return [];
    const activities: any[] = [];
    const requester = this.getRequesterDisplayName(this.ticket);
    const orgName = this.ticket.orgName || 'Certiplate';
    const orgId = this.ticket.orgId || this.ticket.org_id || '-1';
    const env = this.getEnvironmentName();
    const sla = this.getSlaDetails(this.ticket);

    // 1. Ticket Creation & Initial Properties
    activities.push({
      type: 'creation',
      isSystem: false,
      actor: requester,
      time: this.ticket.createdDate,
      timeFormatted: this.formatTimeAgo(this.ticket.createdDate),
      tag: 'Ticket Created',
      items: [
        `Created a new ticket #${this.ticket.id}: "${this.ticket.summary}"`,
        `Added tags: org_id:${orgId}, org_name:${orgName}, env:${env}`,
        `Set contact as ${requester}${this.ticket.requesterEmail ? ' (' + this.ticket.requesterEmail + ')' : ''}, status as ${this.ticket.status || 'Open'}, source as Portal, priority as ${this.ticket.priority || 'Low'}${this.ticket.type && this.ticket.type !== '--' ? ', type as ' + this.ticket.type : ''}`
      ]
    });

    // 2. Resolution Timeline Policy
    activities.push({
      type: 'sla',
      isSystem: true,
      actor: 'Resolution timeline configured',
      time: this.ticket.createdDate,
      timeFormatted: this.formatTimeAgo(this.ticket.createdDate),
      tag: 'Target timeline set',
      items: [
        `Target resolution deadline: ${sla.resolution.date || 'Within 24 hours'}`,
        `Target first response: ${sla.firstResponse.date || 'Within 8 hours'}`
      ]
    });

    // 3. Automation / Routing / Assignment (if assigned)
    if (this.ticket.assigned_to || this.ticket.assignedTo) {
      const assignee = this.ticket.assigned_to || this.ticket.assignedTo;
      activities.push({
        type: 'assignment',
        isSystem: true,
        actor: 'System executed ticket assignment rule',
        time: this.ticket.updated_at || this.ticket.createdDate,
        timeFormatted: this.formatTimeAgo(this.ticket.updated_at || this.ticket.createdDate),
        tag: 'Assigned',
        items: [
          `Assigned ticket to ${assignee}`,
          `Set routing group as Customer Support`
        ]
      });
    }

    // 4. Status Update if Resolved / Closed
    if (this.ticket.status === 'Resolved' || this.ticket.status === 'Closed') {
      activities.push({
        type: 'status',
        isSystem: true,
        actor: 'Ticket status updated to ' + this.ticket.status,
        time: this.ticket.updated_at || this.ticket.createdDate,
        timeFormatted: this.formatTimeAgo(this.ticket.updated_at || this.ticket.createdDate),
        tag: 'Status: ' + this.ticket.status,
        items: [
          `Marked ticket as ${this.ticket.status}`,
          `Resolution SLA marked as completed`
        ]
      });
    }

    return activities;
  }

  getCurrentUser(): string {
    return localStorage.getItem('username') || localStorage.getItem('email') || (this.isSuperAdmin ? 'Admin' : 'User');
  }

  canDeleteComment(comment: any): boolean {
    if (!comment) return false;
    if (this.isSuperAdmin) return true;
    const author = (comment.author_name || comment.created_by || comment.user || '').trim().toLowerCase();
    if (!author) return false;

    const userNames = [
      localStorage.getItem('username'),
      localStorage.getItem('email'),
      localStorage.getItem('name'),
      localStorage.getItem('full_name'),
      this.getCurrentUser()
    ]
      .filter(Boolean)
      .map((u: any) => u.trim().toLowerCase());

    return userNames.includes(author);
  }



  private bindMentionSupport(quill: any, scope: 'main' | 'thread'): void {
    quill.on('text-change', (_delta: any, _old: any, source: string) => {
      if (source !== 'user') return;
      this.handleMentionInput(quill, scope);
    });

    quill.on('selection-change', (range: any) => {
      if (range) {
        this.handleMentionInput(quill, scope);
      } else if (!this.isInteractingWithMentionMenu) {
        this.closeMentionMenu();
      }
    });

    quill.root.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape' && this.mentionMenuOpen) {
        this.closeMentionMenu();
      }
    });
  }

  private handleMentionInput(quill: any, scope: 'main' | 'thread'): void {
    const selection = quill.getSelection();
    if (!selection || selection.length > 0) {
      this.closeMentionMenu();
      return;
    }

    const beforeCursor = quill.getText(0, selection.index);
    const atIndex = beforeCursor.lastIndexOf('@');

    if (atIndex === -1) {
      this.closeMentionMenu();
      return;
    }

    const query = beforeCursor.slice(atIndex + 1);
    if (query.includes('\n') || query.includes(' ')) {
      this.closeMentionMenu();
      return;
    }

    this.mentionMenuOpen = true;
    this.mentionMenuScope = scope;
    this.mentionMenuQuery = query;
    this.mentionInsertMode = 'replace';
    this.mentionRange = { index: atIndex, length: query.length + 1 };
    this.cdr.detectChanges();
  }

  openMentionMenu(scope: 'main' | 'thread' = 'main', fromButton = false): void {
    const quill = scope === 'main' ? this.quill : this.threadQuill;
    if (!quill || !this.mentionUsers.length) return;
    const selection = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
    this.mentionMenuOpen = true;
    this.mentionMenuScope = scope;
    this.mentionInsertMode = fromButton ? 'insert' : 'replace';
    this.mentionMenuQuery = '';
    this.mentionRange = fromButton ? { index: selection.index, length: 0 } : null;
    this.cdr.detectChanges();
  }

  closeMentionMenu(): void {
    this.mentionMenuOpen = false;
    this.isInteractingWithMentionMenu = false;
    this.cdr.detectChanges();
  }

  beginMentionMenuInteraction(): void {
    this.isInteractingWithMentionMenu = true;
  }

  getFilteredMentionUsers(): any[] {
    if (!this.mentionMenuQuery) return this.mentionUsers;
    const q = this.mentionMenuQuery.toLowerCase();
    return this.mentionUsers.filter(u =>
      (u.full_name && u.full_name.toLowerCase().includes(q)) ||
      (u.username && u.username.toLowerCase().includes(q)) ||
      (u.email && u.email.toLowerCase().includes(q))
    );
  }

  mentionDisplayName(user: any): string {
    return user?.full_name || user?.username || user?.email || `User ${user?.id}`;
  }

  selectMentionUser(user: any): void {
    const scope = this.mentionMenuScope;
    const quill = scope === 'main' ? this.quill : this.threadQuill;
    if (!quill || !user) return;
    const displayName = this.mentionDisplayName(user);
    const mentionHtml = `<span class="mention-tag" data-user-id="${user.id}">@${displayName}</span>&nbsp;`;
    
    const targetIds = scope === 'main' ? this.mentionUserIds : this.threadMentionUserIds;
    if (user.id && !targetIds.includes(user.id)) {
      targetIds.push(user.id);
    }

    if (this.mentionInsertMode === 'replace' && this.mentionRange && this.mentionRange.index != null) {
      quill.deleteText(this.mentionRange.index, this.mentionRange.length || 0, 'user');
      quill.clipboard.dangerouslyPasteHTML(this.mentionRange.index, mentionHtml, 'user');
      quill.setSelection(this.mentionRange.index + displayName.length + 2, 0, 'silent');
    } else {
      const selection = quill.getSelection(true) || { index: quill.getLength(), length: 0 };
      quill.clipboard.dangerouslyPasteHTML(selection.index, mentionHtml, 'user');
      quill.setSelection(selection.index + displayName.length + 2, 0, 'silent');
    }

    this.closeMentionMenu();
    quill.focus();
  }

  private async loadStakeholders(): Promise<void> {
    const orgId = this.getOrgId();
    try {
      const res: any = await this.operationService.getStakeholders({ org_id: orgId }).toPromise();
      const list = res?.body?.responseBody || res?.responseBody || res?.body?.data || res?.data || res?.body || [];
      this.mentionUsers = Array.isArray(list) ? list : [];
    } catch {
      this.mentionUsers = [];
    }
  }

  // File Attachments
  openAttachmentPicker(scope: 'main' | 'thread' = 'main'): void {
    if (scope === 'main') {
      this.ticketAttachmentInput?.nativeElement?.click();
    } else {
      this.threadAttachmentInput?.nativeElement?.click();
    }
  }

  handleAttachmentSelection(scope: 'main' | 'thread', event: Event): void {
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);

    if (!files.length) {
      input.value = '';
      return;
    }

    const targetList = scope === 'main' ? this.selectedAttachments : this.threadAttachments;

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        this.toastr.warning(`${file.name} exceeds the maximum allowed file size of 10 MB.`);
        continue;
      }
      if (!targetList.some(f => f.name === file.name && f.size === file.size)) {
        targetList.push(file);
      }
    }

    input.value = '';
    this.cdr.detectChanges();
  }

  removeAttachment(scope: 'main' | 'thread', index: number): void {
    if (scope === 'main') {
      this.selectedAttachments.splice(index, 1);
    } else {
      this.threadAttachments.splice(index, 1);
    }
  }

  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  private async uploadAttachments(files: File[]): Promise<string[]> {
    if (!files.length) return [];
    const uploadedKeys = await Promise.all(
      files.map(async (file) => {
        try {
          const base64 = await this.fileToBase64(file);
          const response = await this.commentService
            .uploadAttachment(base64, file.name, this.COMMENT_ATTACHMENT_FOLDER, file.type)
            .toPromise();
          return response?.body?.responseBody?.key || response?.body?.key || '';
        } catch (e) {
          console.error('Error uploading attachment:', file.name, e);
          return '';
        }
      })
    );
    return uploadedKeys.filter((key) => !!key);
  }

  // Reactions & Thread Actions
  openEmojiPicker(comment: any, event: Event): void {
    event.stopPropagation();
    this.ticketConversations.forEach(c => {
      if (c !== comment) c.showReactions = false;
      if (c.replies) c.replies.forEach((r: any) => { if (r !== comment) r.showReactions = false; });
    });
    comment.showReactions = !comment.showReactions;
  }

  addReaction(comment: any, emoji: string): void {
    comment.reactions = comment.reactions || [];
    const user = this.getCurrentUser();
    const orgId = this.getOrgId();
    const existingIndex = comment.reactions.findIndex((r: any) => (r.user_id || r.user) === user);

    if (existingIndex !== -1) {
      if (comment.reactions[existingIndex].emoji_type === emoji || comment.reactions[existingIndex].emoji === emoji) {
        comment.reactions.splice(existingIndex, 1);
      } else {
        comment.reactions[existingIndex].emoji_type = emoji;
        comment.reactions[existingIndex].emoji = emoji;
      }
    } else {
      comment.reactions.push({
        user_id: user,
        user: user,
        emoji_type: emoji,
        emoji: emoji,
        reacted_at: new Date().toISOString(),
      });
    }

    this.commentService.addReaction({
      comment_id: comment.id,
      user_id: user,
      emoji_type: emoji,
      module: 'ticket',
      id: String(this.ticket.id),
      organization_id: parseInt(orgId, 10),
    }).subscribe({
      error: (err) => console.error('Error saving reaction:', err),
    });

    comment.showReactions = false;
  }

  groupReactions(reactions: any[]): { emoji: string; count: number; users: string[] }[] {
    if (!reactions?.length) return [];
    const map = new Map<string, { count: number; users: string[] }>();
    for (const r of reactions) {
      const emoji = r.emoji_type || r.emoji;
      if (!emoji) continue;
      if (!map.has(emoji)) map.set(emoji, { count: 0, users: [] });
      const entry = map.get(emoji)!;
      entry.count++;
      const user = r.user_id || r.user;
      if (user && !entry.users.includes(user)) entry.users.push(user);
    }
    return Array.from(map.entries()).map(([emoji, v]) => ({ emoji, ...v }));
  }

  openThread(comment: any): void {
    this.activeThread = comment;
    this.cdr.detectChanges();
  }

  closeThread(): void {
    this.activeThread = null;
    this.threadQuill = null;
    this.threadAttachments = [];
    this.threadMentionUserIds = [];
    this.cdr.detectChanges();
  }

  async deleteComment(comment: any): Promise<void> {
    if (!confirm('Are you sure you want to delete this message?')) return;
    const orgId = this.getOrgId();
    const payload = {
      comment_id: comment.id,
      parent_comment_id: comment.parent_comment_id || 0,
      module: 'ticket',
      id: String(this.ticket.id),
      organization_id: parseInt(orgId, 10),
      created_at: comment.created_at,
    };

    try {
      await this.commentService.deleteComment(payload).toPromise();
      this.toastr.success('Message deleted');
      if (this.activeThread && this.activeThread.id === comment.id) {
        this.closeThread();
      }
      await this.loadConversations();
    } catch (err) {
      console.error('Error deleting comment:', err);
      this.toastr.error('Failed to delete message');
    }
  }

  async submitThreadReply(): Promise<void> {
    if (!this.activeThread || !this.ticket) return;

    const html = this.threadQuill?.root?.innerHTML || '';
    const text = this.threadQuill?.getText()?.trim() || '';

    if (!text && this.threadAttachments.length === 0) return;

    this.isSendingThreadReply = true;
    const authorName = this.getCurrentUser();
    const orgId = this.getOrgId();

    try {
      let uploadedKeys: string[] = [];
      if (this.threadAttachments.length > 0) {
        uploadedKeys = await this.uploadAttachments(this.threadAttachments);
      }

      const mentionsToNotify = [...this.threadMentionUserIds];
      await this.commentService.addReply(
        String(this.activeThread.id),
        'ticket',
        String(this.ticket.id),
        orgId,
        html || text,
        authorName,
        uploadedKeys,
        this.threadMentionUserIds
      ).toPromise();

      if (mentionsToNotify.length > 0) {
        await this.sendTicketMentionNotifications(mentionsToNotify, text || html);
      }

      this.toastr.success('Reply sent');
      if (this.threadQuill) {
        this.threadQuill.setText('');
      }
      this.threadAttachments = [];
      this.threadMentionUserIds = [];

      await this.loadConversations();
    } catch (error: any) {
      console.error('Error adding thread reply:', error);
      this.toastr.error('Failed to send reply');
    } finally {
      this.isSendingThreadReply = false;
    }
  }

  private startPolling(): void {
    interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isSendingReply && !this.isSendingThreadReply && this.ticket?.id) {
          this.loadConversations(true);
        }
      });
  }

  goToDashboard(): void {
    this.router.navigate(['/Dashboard']);
  }

  goBack(): void {
    this.backToList.emit();
    this.router.navigate(['/tickets']);
  }

  private isRefreshingAttachments = false;
  private async refreshFreshdeskAttachmentsIfNeeded(): Promise<void> {
    if (!this.ticket || !this.ticket.id || this.isRefreshingAttachments) return;

    const desc = this.ticket.description || '';
    const atts = Array.isArray(this.ticket.attachments) ? this.ticket.attachments : [];

    const hasFreshdeskUrl = desc.includes('freshdesk.com') ||
      atts.some((a: any) => {
        const u = a.attachment_url || a.url || a.signedUrl || '';
        return typeof u === 'string' && u.includes('freshdesk.com');
      });

    if (!hasFreshdeskUrl) return;

    this.isRefreshingAttachments = true;
    try {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Basic ' + btoa(environment.FRESHDESK_API_KEY + ':X'),
        'Accept': 'application/json'
      });
      const fdTicket: any = await this.http.get(`${environment.FRESHDESK_API}/${this.ticket.id}`, { headers }).toPromise();
      if (fdTicket && Array.isArray(fdTicket.attachments) && fdTicket.attachments.length > 0) {
        const freshAtts = fdTicket.attachments;

        if (Array.isArray(this.ticket.attachments)) {
          this.ticket.attachments = this.ticket.attachments.map((oldAtt: any) => {
            const fresh = freshAtts.find((fa: any) => fa.id === oldAtt.id || fa.name === oldAtt.name);
            if (fresh && fresh.attachment_url) {
              return {
                ...oldAtt,
                attachment_url: fresh.attachment_url,
                signedUrl: fresh.attachment_url,
                url: fresh.attachment_url,
                hasError: false
              };
            }
            return oldAtt;
          });
        }

        let updatedDesc = this.ticket.description || '';
        freshAtts.forEach((fa: any) => {
          if (fa.name && fa.attachment_url) {
            const escapedName = fa.name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
            const regex = new RegExp(`https?://[^"'>\\s]*${escapedName}[^"'>\\s]*`, 'g');
            updatedDesc = updatedDesc.replace(regex, fa.attachment_url);
          }
        });
        this.ticket.description = updatedDesc;
        this.cdr.detectChanges();
      }
    } catch (err) {
      console.warn('Could not refresh Freshdesk attachment URLs:', err);
    } finally {
      this.isRefreshingAttachments = false;
    }
  }

  private syncTicketState(): void {
    if (!this.ticket) return;
    this.selectedTicketStatus = this.reverseMapStatus(this.ticket.status);
    this.selectedTicketPriority = this.reverseMapPriority(this.ticket.priority);
    this.selectedTicketType = this.ticket.type || '--';
    this.assignedToEmail = this.ticket.assigned_to || this.ticket.assignedTo || '';
    this.loadConversations();
    this.refreshFreshdeskAttachmentsIfNeeded();
  }

  triggerEdit(): void {
    this.editRequested.emit(this.ticket);
  }

  private getOrgId(): string {
    if (this.ticket?.org_id) return String(this.ticket.org_id);
    if (this.ticket?.orgId) return String(this.ticket.orgId);
    const orgIdEnc = localStorage.getItem('OrgId');
    if (orgIdEnc) {
      const orgId = this.util.decrypt(orgIdEnc);
      if (orgId) return String(orgId);
    }
    return '-1';
  }

  async loadConversations(silent: boolean = false): Promise<void> {
    if (!this.ticket || !this.ticket.id) return;
    const orgId = this.getOrgId();
    try {
      const res: any = await this.commentService.getComments('ticket', String(this.ticket.id), orgId).toPromise();
      const list = res?.body?.data || res?.data || (Array.isArray(res) ? res : []);
      
      const attachmentKeys: string[] = [];

      const mapCommentItem = (item: any): any => {
        const isCurrentSuperAdmin = item.created_by && (
          item.created_by.toLowerCase().includes('admin') ||
          item.created_by.toLowerCase().includes('support') ||
          (this.isSuperAdmin && item.created_by.toLowerCase() === (localStorage.getItem('username') || '').toLowerCase())
        );
        const atts = item.attachment_ids || item.attachments || [];
        if (Array.isArray(atts)) {
          atts.forEach((k: string) => { if (k && typeof k === 'string') attachmentKeys.push(k); });
        }

        const replies = (item.replies || [])
          .filter((r: any) => !r.is_deleted)
          .map((r: any) => mapCommentItem(r));

        return {
          id: item.comment_id || item.id,
          parent_comment_id: item.parent_comment_id || 0,
          author_name: item.created_by || item.author_name || (isCurrentSuperAdmin ? 'Support Team' : this.getRequesterDisplayName(this.ticket)),
          created_at: item.created_at,
          body: item.comment_text || item.body || item.body_text || '',
          body_text: item.comment_text ? item.comment_text.replace(/<[^>]*>/g, '') : (item.body_text || ''),
          is_admin: isCurrentSuperAdmin,
          reactions: item.reactions || [],
          attachments: atts,
          attachmentDetails: [],
          replies,
          hovered: false,
          showReactions: false
        };
      };

      if (Array.isArray(list) && list.length > 0) {
        const filtered = list.filter((c: any) => !c.is_deleted);
        const transformed = filtered.map(mapCommentItem);
        transformed.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

        // Fetch signed URLs for comment attachments if any
        if (attachmentKeys.length > 0) {
          try {
            const urlRes: any = await this.commentService.getSignedUrls(Array.from(new Set(attachmentKeys))).toPromise();
            const urlMap = urlRes?.body?.responseBody?.signedUrls || urlRes?.responseBody?.signedUrls || urlRes?.body?.responseBody || urlRes?.responseBody || {};
            
            const attachUrls = (c: any) => {
              c.attachmentDetails = (c.attachments || []).map((key: string) => ({
                key,
                name: this.getAttachmentNameFromKey(key),
                signedUrl: urlMap[key] || ''
              }));
              if (c.replies) c.replies.forEach(attachUrls);
            };

            transformed.forEach(attachUrls);
          } catch {
            const attachUrls = (c: any) => {
              c.attachmentDetails = (c.attachments || []).map((key: string) => ({
                key,
                name: this.getAttachmentNameFromKey(key),
                signedUrl: ''
              }));
              if (c.replies) c.replies.forEach(attachUrls);
            };
            transformed.forEach(attachUrls);
          }
        }

        this.ticketConversations = transformed;

        // Keep activeThread in sync if open
        if (this.activeThread) {
          const updatedThread = this.ticketConversations.find(c => c.id === this.activeThread.id);
          if (updatedThread) {
            this.activeThread = updatedThread;
          }
        }
      } else {
        // Fall back to Freshdesk conversations
        try {
          const freshdeskConvs: any = await this.http.get<any[]>(`${environment.FRESHDESK_API}/${this.ticket.id}/conversations`, {
            headers: this.getFreshdeskHeaders()
          }).toPromise();

          if (Array.isArray(freshdeskConvs) && freshdeskConvs.length > 0) {
            this.ticketConversations = freshdeskConvs
              .filter((c: any) => (c.body || c.body_text) && !c.private)
              .map((c: any) => ({
                id: c.id,
                parent_comment_id: 0,
                author_name: c.from_email || (c.user_id ? 'Support Team' : this.getRequesterDisplayName(this.ticket)),
                created_at: c.created_at || c.updated_at,
                body: c.body || c.body_text || '',
                body_text: c.body_text || (c.body ? c.body.replace(/<[^>]*>/g, '') : ''),
                is_admin: !!c.user_id,
                reactions: [],
                attachments: (c.attachments || []).map((att: any) => att.attachment_url || att.url),
                attachmentDetails: (c.attachments || []).map((att: any) => ({
                  key: att.id,
                  name: att.name,
                  signedUrl: att.attachment_url || att.url
                })),
                replies: [],
                hovered: false,
                showReactions: false
              }));
          } else {
            this.ticketConversations = [];
          }
        } catch {
          this.ticketConversations = [];
        }
      }
    } catch (e) {
      if (!silent) console.warn('Could not load ticket comments from Cassandra:', e);
      try {
        const freshdeskConvs: any = await this.http.get<any[]>(`${environment.FRESHDESK_API}/${this.ticket.id}/conversations`, {
          headers: this.getFreshdeskHeaders()
        }).toPromise();

        if (Array.isArray(freshdeskConvs) && freshdeskConvs.length > 0) {
          this.ticketConversations = freshdeskConvs
            .filter((c: any) => (c.body || c.body_text) && !c.private)
            .map((c: any) => ({
              id: c.id,
              parent_comment_id: 0,
              author_name: c.from_email || (c.user_id ? 'Support Team' : this.getRequesterDisplayName(this.ticket)),
              created_at: c.created_at || c.updated_at,
              body: c.body || c.body_text || '',
              body_text: c.body_text || (c.body ? c.body.replace(/<[^>]*>/g, '') : ''),
              is_admin: !!c.user_id,
              reactions: [],
              attachments: (c.attachments || []).map((att: any) => att.attachment_url || att.url),
              attachmentDetails: (c.attachments || []).map((att: any) => ({
                key: att.id,
                name: att.name,
                signedUrl: att.attachment_url || att.url
              })),
              replies: [],
              hovered: false,
              showReactions: false
            }));
        } else {
          this.ticketConversations = [];
        }
      } catch {
        if (!silent) this.ticketConversations = [];
      }
    }
  }

  async sendTicketReply(): Promise<void> {
    if (!this.ticket) return;

    const html = this.quill?.root?.innerHTML || '';
    const text = this.quill?.getText()?.trim() || '';

    if (!text && this.selectedAttachments.length === 0) {
      return;
    }

    this.isSendingReply = true;
    const authorName = localStorage.getItem('username') || (this.isSuperAdmin ? 'Support Team' : this.getRequesterDisplayName(this.ticket));
    const orgId = this.getOrgId();

    try {
      let uploadedKeys: string[] = [];
      if (this.selectedAttachments.length > 0) {
        uploadedKeys = await this.uploadAttachments(this.selectedAttachments);
      }

      const mentionsToNotify = [...this.mentionUserIds];

      if (this.ticket.isFreshdesk) {
        try {
          await this.http.post(`${environment.FRESHDESK_API}/${this.ticket.id}/reply`, {
            body: html || text
          }, {
            headers: this.getFreshdeskHeaders()
          }).toPromise();
        } catch (fErr) {
          console.warn('Could not send reply to Freshdesk:', fErr);
        }
      }

      try {
        await this.commentService.addCommentWithAttachments(
          'ticket',
          String(this.ticket.id),
          orgId,
          html || text,
          authorName,
          this.ticket.status || 'open',
          uploadedKeys,
          this.mentionUserIds
        ).toPromise();
      } catch (cErr) {
        // Cassandra comments not configured for tickets
      }

      if (mentionsToNotify.length > 0) {
        await this.sendTicketMentionNotifications(mentionsToNotify, text || html);
      }

      this.toastr.success('Reply sent successfully');
      
      if (this.quill) {
        this.quill.setText('');
      }
      this.selectedAttachments = [];
      this.mentionUserIds = [];

      await this.loadConversations();
    } catch (error: any) {
      console.error('Error sending reply:', error);
      this.toastr.error('Failed to send reply');
    } finally {
      this.isSendingReply = false;
    }
  }

  private async sendTicketMentionNotifications(mentionIds: Array<string | number>, commentText: string): Promise<void> {
    if (!mentionIds || !mentionIds.length) return;
    const recipients = (this.mentionUsers || [])
      .filter((u: any) => mentionIds.some(id => String(id) === String(u.id)))
      .map((u: any) => ({
        email: u.email,
        full_name: u.full_name || u.username || u.name || u.email,
        username: u.username || ''
      }))
      .filter((r: any) => !!r.email);

    if (!recipients.length) return;

    try {
      const orgId = this.getOrgId();
      const payload = {
        requestBody: {
          recipients,
          subject: `${localStorage.getItem('username') || 'Someone'} mentioned you on ticket #${this.ticket?.id || ''}`,
          comment: commentText,
          commenter_name: localStorage.getItem('username') || (this.isSuperAdmin ? 'Support Team' : this.getRequesterDisplayName(this.ticket)),
          ticket_id: this.ticket?.id,
          ticket_subject: this.ticket?.subject || this.ticket?.summary || 'Support Ticket',
          ticket_status: this.ticket?.status_name || this.ticket?.status || 'Open',
          ticket_priority: this.ticket?.priority_name || this.ticket?.priority || 'Normal',
          ticket_url: window.location.href,
          org_name: this.ticket?.orgName || this.ticket?.organization_name || 'Certiplate',
          org_id: orgId || this.ticket?.org_id || this.ticket?.orgId || null
        }
      };
      await this.api.post('/sendTicketMentionEmail', payload).toPromise();
    } catch (err) {
      console.warn('Could not send mention notification email:', err);
    }
  }

  showAllAttachments: boolean = false;
  isAttachmentsCollapsed: boolean = false;

  toggleShowAllAttachments(event?: Event): void {
    if (event) event.stopPropagation();
    this.showAllAttachments = !this.showAllAttachments;
  }

  toggleAttachmentsCollapse(): void {
    this.isAttachmentsCollapsed = !this.isAttachmentsCollapsed;
  }

  getSanitizedDescriptionText(description: string): SafeHtml {
    if (!description) return '';
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(description, 'text/html');
      const images = doc.querySelectorAll('img');
      images.forEach((img) => {
        img.remove();
      });
      return doc.body.innerHTML.trim();
    } catch {
      return description;
    }
  }

  getAllTicketAttachments(): any[] {
    const list: any[] = [];
    const seenIdentifiers = new Set<string>();

    if (Array.isArray(this.ticket?.attachments)) {
      this.ticket.attachments.forEach((att: any, idx: number) => {
        const url = att.signedUrl || att.url || att.attachment_url || att.key;
        const name = att.name || this.getAttachmentNameFromKey(url || '') || `Attachment_${idx + 1}`;
        if (url || name) {
          const ext = this.getAttachmentExtension(name || url);
          const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
          const identifier = (name || url).toLowerCase();
          seenIdentifiers.add(identifier);
          list.push({
            ...att,
            name,
            url: url || '',
            signedUrl: url || '',
            createdDate: att.createdDate || att.created_at || this.ticket.createdDate,
            isImage: isImg,
            hasError: !!att.hasError
          });
        }
      });
    }

    if (this.ticket?.description) {
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(this.ticket.description, 'text/html');
        const images = doc.querySelectorAll('img');
        images.forEach((img, idx) => {
          const src = img.getAttribute('src');
          const alt = img.getAttribute('alt') || this.getAttachmentNameFromKey(src || '') || `Image_${idx + 1}.png`;
          const identifier = (alt || src || '').toLowerCase();
          if (src && !seenIdentifiers.has(identifier) && !seenIdentifiers.has(src.toLowerCase())) {
            seenIdentifiers.add(identifier);
            seenIdentifiers.add(src.toLowerCase());
            list.push({
              name: alt.endsWith('.png') || alt.endsWith('.jpg') || alt.endsWith('.jpeg') || alt.endsWith('.webp') || alt.endsWith('.gif') ? alt : `${alt}.png`,
              url: src,
              signedUrl: src,
              createdDate: this.ticket.createdDate,
              isImage: true,
              hasError: false
            });
          }
        });
      } catch (e) {
        console.warn('Failed to parse description images:', e);
      }
    }

    return list;
  }

  onAttachmentImgError(att: any): void {
    if (att) {
      att.hasError = true;
      this.cdr.detectChanges();
    }
  }

  onPreviewImageError(): void {
    this.previewMode = 'unsupported';
    this.previewMessage = 'Image could not be loaded or the link has expired.';
    this.cdr.detectChanges();
  }

  getVisibleTicketAttachments(): any[] {
    const all = this.getAllTicketAttachments();
    if (this.showAllAttachments || all.length <= 2) {
      return all;
    }
    return all.slice(0, 2);
  }

  isImageAttachment(att: any): boolean {
    if (att?.isImage) return true;
     const contentType = (att?.contentType || att?.mimeType || att?.type || '').toLowerCase();
    if (contentType.startsWith('image/')) return true;
    const name = att?.name || att?.url || att?.key || att?.signedUrl || '';
    const ext = this.getAttachmentExtension(name);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
  }

  getFileIconClass(fileName: string): string {
    const ext = this.getAttachmentExtension(fileName || '');
    if (ext === 'pdf') return 'fa-file-pdf-o';
    if (['doc', 'docx'].includes(ext)) return 'fa-file-word-o';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return 'fa-file-excel-o';
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'fa-file-archive-o';
    if (['mp4', 'webm', 'mov'].includes(ext)) return 'fa-file-video-o';
    if (['txt', 'log'].includes(ext)) return 'fa-file-text-o';
    return 'fa-file-o';
  }

  getSanitizedDescription(description: string): SafeHtml {
    if (!description) return '';
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(description, 'text/html');
      const images = doc.querySelectorAll('img');
      images.forEach((img, idx) => {
        const src = img.getAttribute('src');
        const alt = img.getAttribute('alt') || `Image ${idx + 1}`;
        if (src) {
          const thumbBox = doc.createElement('div');
          thumbBox.setAttribute('class', 'fd-description-img-thumbnail-box');
          thumbBox.setAttribute('data-img-src', src);
          thumbBox.setAttribute('data-img-alt', alt);
          thumbBox.setAttribute('title', 'Click to view full image');
          thumbBox.innerHTML = `
            <img src="${src}" alt="${alt}" class="fd-thumb-img" loading="lazy" onerror="this.style.display='none'; if(this.nextElementSibling) this.nextElementSibling.style.display='flex';" />
            <div class="fd-thumb-fallback" style="display:none; align-items:center; justify-content:center; width:100%; height:100%; min-height:80px; background:#f4f5f7; color:#6b778c; font-size:12px; border-radius:4px; padding:8px;">
              <i class="fa fa-file-image-o" style="margin-right:6px; font-size:16px;"></i> <span>${alt}</span>
            </div>
            <div class="fd-thumb-overlay">
              <i class="fa fa-search-plus"></i>
              <span>View</span>
            </div>
          `;
          img.parentNode?.replaceChild(thumbBox, img);
        }
      });
      return doc.body.innerHTML;
    } catch {
      return description;
    }
  }

  handleContentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target) return;

    const imgBtn = target.closest('[data-img-src]') as HTMLElement;
    if (imgBtn) {
      event.preventDefault();
      event.stopPropagation();
      const src = imgBtn.getAttribute('data-img-src') || '';
      const alt = imgBtn.getAttribute('data-img-alt') || 'Image Preview';
      if (src) {
        this.openAttachmentPreview({
          signedUrl: src,
          url: src,
          name: alt.endsWith('.png') || alt.endsWith('.jpg') || alt.endsWith('.jpeg') || alt.endsWith('.gif') || alt.endsWith('.webp') ? alt : `${alt}.png`
        }, event);
      }
      return;
    }

    if (target.tagName === 'IMG') {
      const img = target as HTMLImageElement;
      if (img.src) {
        event.preventDefault();
        event.stopPropagation();
        this.openAttachmentPreview({
          signedUrl: img.src,
          url: img.src,
          name: img.alt || 'Image Preview'
        }, event);
      }
    }
  }

  async updateTicketProperties(): Promise<void> {
    if (!this.ticket) return;

    this.isUpdatingProperties = true;
    try {
      const trimmedAssignedTo = this.assignedToEmail ? this.assignedToEmail.trim() : null;
      const freshdeskPayload: any = {
        status: +this.selectedTicketStatus,
        priority: +this.selectedTicketPriority,
      };
      if (this.selectedTicketType && this.selectedTicketType !== '--') {
        freshdeskPayload.type = this.selectedTicketType;
      }

      const updatePayload = {
        status: +this.selectedTicketStatus,
        priority: +this.selectedTicketPriority,
        type: this.selectedTicketType !== '--' ? this.selectedTicketType : undefined,
        assigned_to: trimmedAssignedTo
      };

      if (this.ticket.isFreshdesk) {
        await this.http.put(`${environment.FRESHDESK_API}/${this.ticket.id}`, freshdeskPayload, {
          headers: this.getFreshdeskHeaders()
        }).toPromise();
      } else {
        try {
          await this.api.put(`/tickets/${this.ticket.id}`, updatePayload).toPromise();
        } catch (apiErr: any) {
          if (apiErr?.status === 404) {
            await this.http.put(`${environment.FRESHDESK_API}/${this.ticket.id}`, freshdeskPayload, {
              headers: this.getFreshdeskHeaders()
            }).toPromise();
          } else {
            throw apiErr;
          }
        }
      }

      this.ticket.status = this.mapStatus(+this.selectedTicketStatus);
      this.ticket.priority = this.mapPriority(+this.selectedTicketPriority);
      this.ticket.type = this.selectedTicketType;
      this.ticket.assigned_to = trimmedAssignedTo;
      this.ticket.assignedTo = trimmedAssignedTo;

      this.toastr.success('Ticket properties updated');
      this.ticketUpdated.emit(this.ticket);
    } catch (error: any) {
      console.error('Error updating ticket properties:', error);
      this.toastr.error('Failed to update properties');
    } finally {
      this.isUpdatingProperties = false;
    }
  }

  getRequesterDisplayName(ticket: any): string {
    if (!ticket) return 'User';
    if (ticket.requesterName && ticket.requesterName !== 'Unknown' && ticket.requesterName !== 'User') {
      return ticket.requesterName;
    }
    if (ticket.requesterEmail) {
      const namePart = ticket.requesterEmail.split('@')[0];
      return namePart.replace(/[._]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
    }
    return 'User';
  }

  getAvatarInitial(name: string): string {
    if (!name) return 'U';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  getAvatarColor(name: string): string {
    const colors = [
      '#ef4444', '#f97316', '#f59e0b', '#10b981', '#06b6d4',
      '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#14b8a6'
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }

  isTicketOverdue(ticket: any): boolean {
    if (!ticket || !ticket.createdDate) return false;
    if (ticket.status === 'Resolved' || ticket.status === 'Closed') return false;
    const created = new Date(ticket.createdDate).getTime();
    const now = new Date().getTime();
    const hoursElapsed = (now - created) / (1000 * 60 * 60);
    return hoursElapsed > 24;
  }

  getSlaDetails(ticket: any): { firstResponse: { text: string; date: string; isOverdue: boolean }; resolution: { text: string; date: string; isOverdue: boolean } } {
    if (!ticket || !ticket.createdDate) {
      return {
        firstResponse: { text: 'First response due soon', date: '', isOverdue: false },
        resolution: { text: 'Resolution due soon', date: '', isOverdue: false }
      };
    }

    const created = new Date(ticket.createdDate);
    const now = new Date();
    const firstResponseDue = new Date(created.getTime() + 8 * 60 * 60 * 1000);
    const resolutionDue = new Date(created.getTime() + 24 * 60 * 60 * 1000);

    const formatSlaDate = (d: Date) => {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const dayName = days[d.getDay()];
      const monthName = months[d.getMonth()];
      const dateNum = d.getDate();
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      return `${dayName}, ${dateNum} ${monthName} ${year}, ${hours}:${mins}`;
    };

    const firstResponseOverdue = now > firstResponseDue;
    const resolutionOverdue = now > resolutionDue && ticket.status !== 'Resolved' && ticket.status !== 'Closed';

    let firstResponseText = '';
    if (firstResponseOverdue) {
      const hoursOver = Math.round((now.getTime() - firstResponseDue.getTime()) / (1000 * 60 * 60));
      firstResponseText = hoursOver >= 24 ? `First response overdue by ${Math.round(hoursOver / 24)}d` : `First response overdue by ${hoursOver}h`;
    } else {
      const hoursLeft = Math.max(1, Math.round((firstResponseDue.getTime() - now.getTime()) / (1000 * 60 * 60)));
      firstResponseText = `First response due in ${hoursLeft} hours`;
    }

    let resolutionText = '';
    if (ticket.status === 'Resolved' || ticket.status === 'Closed') {
      resolutionText = 'Resolution completed';
    } else if (resolutionOverdue) {
      const hoursOver = Math.round((now.getTime() - resolutionDue.getTime()) / (1000 * 60 * 60));
      resolutionText = hoursOver >= 24 ? `Resolution overdue by ${Math.round(hoursOver / 24)}d` : `Resolution overdue by ${hoursOver}h`;
    } else {
      const hoursLeft = Math.max(1, Math.round((resolutionDue.getTime() - now.getTime()) / (1000 * 60 * 60)));
      resolutionText = `Resolution due in ${hoursLeft} hours`;
    }

    return {
      firstResponse: {
        text: firstResponseText,
        date: formatSlaDate(firstResponseDue),
        isOverdue: firstResponseOverdue
      },
      resolution: {
        text: resolutionText,
        date: formatSlaDate(resolutionDue),
        isOverdue: resolutionOverdue
      }
    };
  }

  private mapStatus(statusNum: number): string {
    switch (statusNum) {
      case 2: return 'Open';
      case 3: return 'Pending';
      case 4: return 'Resolved';
      case 5: return 'Closed';
      default: return 'Open';
    }
  }

  private reverseMapStatus(statusStr: string): number {
    switch (statusStr) {
      case 'Open': return 2;
      case 'Pending': return 3;
      case 'Resolved': return 4;
      case 'Closed': return 5;
      default: return 2;
    }
  }

  private mapPriority(priorityNum: number): string {
    switch (priorityNum) {
      case 1: return 'Low';
      case 2: return 'Medium';
      case 3: return 'High';
      case 4: return 'Urgent';
      default: return 'Low';
    }
  }

  private reverseMapPriority(priorityStr: string): number {
    switch (priorityStr) {
      case 'Low': return 1;
      case 'Medium': return 2;
      case 'High': return 3;
      case 'Urgent': return 4;
      default: return 1;
    }
  }

  getAttachmentExtension(fileNameOrKey: string): string {
    const cleanName = (fileNameOrKey || '').split('?')[0].split('#')[0];
    const index = cleanName.lastIndexOf('.');
    return index >= 0 ? cleanName.substring(index + 1).toLowerCase() : '';
  }

  getPreviewMode(fileName: string, attachment?: any): string {
    const contentType = (attachment?.contentType || attachment?.mimeType || attachment?.type || '').toLowerCase();
    if (contentType.startsWith('image/')) return 'image';
    if (contentType.includes('pdf')) return 'pdf';
    if (contentType.includes('csv')) return 'csv';
    if (contentType.includes('json')) return 'json';

    const ext = this.getAttachmentExtension(fileName || attachment?.name || attachment?.signedUrl || attachment?.url || attachment?.key || '');
    if (ext === 'pdf') return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
    if (['txt', 'log'].includes(ext)) return 'text';
    if (['csv'].includes(ext)) return 'csv';
    if (['json'].includes(ext)) return 'json';
    if (['docx'].includes(ext)) return 'docx';
    if (['xls', 'xlsx'].includes(ext)) return 'excel';
    if (['mp4', 'webm', 'mov', 'ogg'].includes(ext)) return 'video';

    return 'unsupported';
  }

  getAttachmentNameFromKey(key: string): string {
    if (!key) return '';
    const cleanKey = key.split('?')[0].split('#')[0];
    const parts = cleanKey.split('/');
    const rawName = parts[parts.length - 1] || '';
    const underscoreIndex = rawName.indexOf('_');
    return underscoreIndex >= 0 ? rawName.substring(underscoreIndex + 1) : rawName;
  }

  private splitCsvLine(line: string): string[] {
    const cells: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const next = line[i + 1];

      if (char === '"' && inQuotes && next === '"') {
        current += '"';
        i++;
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        cells.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    cells.push(current.trim());
    return cells;
  }

  private parseCsv(text: string): { headers: string[]; rows: string[][] } {
    const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
    if (!lines.length) {
      return { headers: [], rows: [] };
    }

    const headers = this.splitCsvLine(lines[0]);
    const rows = lines.slice(1).map(line => this.splitCsvLine(line));
    return { headers, rows };
  }

  private previewBlobUrl: string | null = null;

  async openAttachmentPreview(attachment: any, event?: Event): Promise<void> {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.previewBlobUrl) {
      window.URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = null;
    }

    const key = attachment?.key || (typeof attachment === 'string' ? attachment : '');
    let url = attachment?.signedUrl || attachment?.url || attachment?.attachment_url || key;

    if (!url && !key) {
      return;
    }

    if (url && typeof url === 'string' && url.includes('freshdesk.com') && this.ticket?.id) {
      try {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(environment.FRESHDESK_API_KEY + ':X'),
          'Accept': 'application/json'
        });
        const fdTicket: any = await this.http.get(`${environment.FRESHDESK_API}/${this.ticket.id}`, { headers }).toPromise();
        if (fdTicket && Array.isArray(fdTicket.attachments)) {
          const fresh = fdTicket.attachments.find((fa: any) => fa.name === attachment?.name || fa.id === attachment?.id);
          if (fresh && fresh.attachment_url) {
            url = fresh.attachment_url;
            if (attachment && typeof attachment === 'object') {
              attachment.signedUrl = fresh.attachment_url;
              attachment.url = fresh.attachment_url;
              attachment.attachment_url = fresh.attachment_url;
            }
          }
        }
      } catch (e) {
        console.warn('Failed to refresh signed URL before preview:', e);
      }
    }

    this.previewAttachment = attachment;
    this.previewFileName =
      attachment?.name ||
      this.getAttachmentNameFromKey(key || url || '') ||
      'attachment';

    this.previewFileType = this.getAttachmentExtension(this.previewFileName);
    this.previewMode = this.getPreviewMode(this.previewFileName || key || url || '');

    this.previewUrl = '';
    this.previewResourceUrl = null;
    this.previewText = '';
    this.previewCsvHeaders = [];
    this.previewCsvRows = [];
    this.previewJsonText = '';
    this.previewDocxText = '';
    this.previewExcelHeaders = [];
    this.previewExcelRows = [];
    this.previewMessage = '';
    this.previewLoading = true;
    this.attachmentPreviewOpen = true;

    try {
      const isFullHttpUrl = url && (url.startsWith('http://') || url.startsWith('https://')) && !url.includes('localhost:4200/local/');
      let finalUrl = url;
      let fileBlob: Blob | null = null;

      if (!isFullHttpUrl && (key || url)) {
        const fileKeyToFetch = key || url;
        try {
          const res: any = await this.commentService.downloadAttachment(fileKeyToFetch).toPromise();
          fileBlob = res?.body instanceof Blob ? res.body : new Blob([res?.body]);
          this.previewBlobUrl = window.URL.createObjectURL(fileBlob);
          finalUrl = this.previewBlobUrl;
        } catch (downloadErr) {
          console.warn('Failed to download blob for preview key:', fileKeyToFetch, downloadErr);
        }
      }

      this.previewUrl = finalUrl;

      if (
        this.previewMode === 'text' ||
        this.previewMode === 'csv' ||
        this.previewMode === 'json'
      ) {
        let content = '';
        if (fileBlob) {
          content = await fileBlob.text();
        } else {
          const response = await fetch(finalUrl);
          if (!response.ok) {
            throw new Error(`Preview fetch failed: ${response.status}`);
          }
          content = await response.text();
        }

        if (this.previewMode === 'text') {
          this.previewText = content;
        } else if (this.previewMode === 'csv') {
          const parsed = this.parseCsv(content);
          this.previewCsvHeaders = parsed.headers;
          this.previewCsvRows = parsed.rows;
        } else if (this.previewMode === 'json') {
          this.previewJsonText = JSON.stringify(JSON.parse(content), null, 2);
        }
      } else if (this.previewMode === 'docx') {
        let arrayBuffer: ArrayBuffer;
        if (fileBlob) {
          arrayBuffer = await fileBlob.arrayBuffer();
        } else {
          const response = await fetch(finalUrl);
          if (!response.ok) {
            throw new Error(`Preview fetch failed: ${response.status}`);
          }
          arrayBuffer = await response.arrayBuffer();
        }
        const result = await (mammoth as any).extractRawText({ arrayBuffer });
        this.previewDocxText = result.value;
      } else if (this.previewMode === 'excel') {
        let arrayBuffer: ArrayBuffer;
        if (fileBlob) {
          arrayBuffer = await fileBlob.arrayBuffer();
        } else {
          const response = await fetch(finalUrl);
          if (!response.ok) {
            throw new Error(`Preview fetch failed: ${response.status}`);
          }
          arrayBuffer = await response.arrayBuffer();
        }
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const data: any[][] = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        this.previewExcelHeaders = data.length ? data[0].map(String) : [];
        this.previewExcelRows = data.length > 1 ? data.slice(1) : [];
      } else if (this.previewMode === 'pdf') {
        this.previewResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(finalUrl);
      } else if (this.previewMode === 'image') {
        this.previewUrl = finalUrl;
      } else if (this.previewMode === 'video') {
        this.previewUrl = finalUrl;
      } else {
        this.previewMode = 'unsupported';
        this.previewMessage = 'Preview is not available for this attachment.';
      }
    } catch (err) {
      console.error('Error loading attachment preview:', err);
      this.previewMode = 'unsupported';
      this.previewMessage = 'Unable to load preview. Please download the attachment.';
    } finally {
      this.previewLoading = false;
      this.cdr.detectChanges();
    }
  }

  closeAttachmentPreview(): void {
    if (this.previewBlobUrl) {
      window.URL.revokeObjectURL(this.previewBlobUrl);
      this.previewBlobUrl = null;
    }
    this.attachmentPreviewOpen = false;
    this.previewLoading = false;
    this.previewFileName = '';
    this.previewFileType = '';
    this.previewMode = 'unsupported';
    this.previewUrl = '';
    this.previewResourceUrl = null;
    this.previewText = '';
    this.previewCsvHeaders = [];
    this.previewCsvRows = [];
    this.previewJsonText = '';
    this.previewDocxText = '';
    this.previewExcelHeaders = [];
    this.previewExcelRows = [];
    this.previewMessage = '';
    this.previewAttachment = null;
  }

  async downloadAttachmentFromPreview(): Promise<void> {
    const attachmentKey = this.previewAttachment?.key || '';
    const fileName = this.previewFileName || 'attachment';

    if (attachmentKey) {
      try {
        const response: any = await this.commentService.downloadAttachment(attachmentKey).toPromise();
        const blob = response?.body instanceof Blob ? response.body : new Blob([response?.body]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 1000);
        return;
      } catch (err) {
        console.warn('Backend download failed, attempting fallback URL download:', err);
      }
    }

    const url = this.previewUrl || this.previewAttachment?.signedUrl || this.previewAttachment?.url || this.previewAttachment?.attachment_url;
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }
}
